import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  GetApp as DownloadIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import versionService from '../../services/versionService';
import CustomPagination from '../../components/common/CustomPagination';

// Add serial numbers to data
const addSerialNumbers = (data, page, rowsPerPage) => {
  if (!Array.isArray(data)) {
    console.warn('addSerialNumbers: data is not an array:', data);
    return [];
  }
  return data.map((row, idx) => ({
    ...row,
    sn: (page * rowsPerPage) + idx + 1
  }));
};

// Debounce hook
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

const VersionManagement = () => {
  const { t } = useTranslation(['versionManagement', 'common']);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingVersion, setEditingVersion] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filter state
  const [filterSearch, setFilterSearch] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Debounced filter values
  const debouncedSearch = useDebounce(filterSearch, 400);
  
  const [formData, setFormData] = useState({
    version_code: '',
    version_name: '',
    platform: 'expo',
    is_required: false,
    is_latest: false,
    release_notes: '',
    min_supported_version: ''
  });

  // Fetch versions with pagination and filters
  const fetchVersions = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        page_size: rowsPerPage,
        search: debouncedSearch || undefined,
        platform: filterPlatform !== 'all' ? filterPlatform : undefined,
        is_latest: filterStatus === 'latest' ? true : filterStatus === 'required' ? undefined : undefined,
        is_required: filterStatus === 'required' ? true : undefined
      };
      
      const response = await versionService.getVersions(params);
      const versionsWithSerial = addSerialNumbers(response.results || [], page, rowsPerPage);
      
      setVersions(versionsWithSerial);
      setTotalCount(response.count || 0);
      setTotalPages(Math.ceil((response.count || 0) / rowsPerPage) || 1);
    } catch (error) {
      console.error('Error fetching versions:', error);
      setSnackbar({
        open: true,
        message: t('versionManagement:messages.errorFetchingVersions'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch, filterPlatform, filterStatus]);

  useEffect(() => {
    fetchVersions();
  }, [fetchVersions]);

  const handleOpenDialog = (version = null) => {
    if (version) {
      setEditingVersion(version);
      setFormData(version);
    } else {
      setEditingVersion(null);
      setFormData({
        version_code: '',
        version_name: '',
        platform: 'expo',
        is_required: false,
        is_latest: false,
        release_notes: '',
        min_supported_version: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingVersion(null);
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      if (editingVersion) {
        await versionService.updateVersion(editingVersion.id, formData);
        setSnackbar({
          open: true,
          message: t('versionManagement:messages.versionUpdatedSuccess'),
          severity: 'success'
        });
      } else {
        await versionService.createVersion(formData);
        setSnackbar({
          open: true,
          message: t('versionManagement:messages.versionCreatedSuccess'),
          severity: 'success'
        });
      }
      
      handleCloseDialog();
      fetchVersions();
    } catch (error) {
      console.error('Error saving version:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || t('versionManagement:messages.errorSavingVersion'),
        severity: 'error'
      });
    }
  };

  const handleDelete = async (versionId) => {
    if (window.confirm(t('versionManagement:messages.deleteConfirm'))) {
      try {
        await versionService.deleteVersion(versionId);
        setSnackbar({
          open: true,
          message: t('versionManagement:messages.versionDeletedSuccess'),
          severity: 'success'
        });
        fetchVersions();
      } catch (error) {
        console.error('Error deleting version:', error);
        setSnackbar({
          open: true,
          message: t('versionManagement:messages.errorDeletingVersion'),
          severity: 'error'
        });
      }
    }
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleCustomRowsInputChange = (e) => {
    setCustomRowsInput(e.target.value.replace(/\D/g, ''));
  };

  const handleCustomRowsInputBlur = () => {
    const val = parseInt(customRowsInput, 10);
    if (val && val > 0) {
      setRowsPerPage(val);
      setPage(0);
    }
    setCustomRowsActive(false);
  };

  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(rowsPerPage.toString());
    } else {
      setRowsPerPage(Number(value));
      setPage(0);
      setCustomRowsActive(false);
    }
  };

  // Filter handlers
  const handleFilterSearchChange = (e) => setFilterSearch(e.target.value);
  const handleFilterPlatformChange = (e) => setFilterPlatform(e.target.value);
  const handleFilterStatusChange = (e) => setFilterStatus(e.target.value);

  const getPlatformIcon = (platform) => {
    const icons = {
      android: '🤖',
      ios: '🍎',
      expo: '⚡',
      web: '🌐'
    };
    return icons[platform] || '📱';
  };

  const getStatusChip = (version) => {
    if (version.is_latest) {
      return <Chip icon={<CheckIcon />} label={t('versionManagement:status.latest')} color="success" size="small" />;
    }
    if (version.is_required) {
      return <Chip icon={<WarningIcon />} label={t('versionManagement:status.required')} color="error" size="small" />;
    }
    return <Chip label={t('versionManagement:status.available')} color="default" size="small" />;
  };

  const columns = [
    {
      field: 'sn',
      headerName: t('versionManagement:table.sn'),
      width: 60,
      sortable: false,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold">
          {params.value}
        </Typography>
      )
    },
    {
      field: 'version_code',
      headerName: t('versionManagement:table.versionCode'),
      width: 120,
      renderCell: (params) => (
        <Typography variant="subtitle2" fontWeight="bold">
          {params.value}
        </Typography>
      )
    },
    {
      field: 'version_name',
      headerName: t('versionManagement:table.versionName'),
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'platform',
      headerName: t('versionManagement:table.platform'),
      width: 120,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <span>{getPlatformIcon(params.value)}</span>
          <Typography variant="capitalize">
            {params.value}
          </Typography>
        </Box>
      )
    },
    {
      field: 'status',
      headerName: t('versionManagement:table.status'),
      width: 120,
      renderCell: (params) => getStatusChip(params.row)
    },
    {
      field: 'release_notes',
      headerName: t('versionManagement:table.releaseNotes'),
      width: 200,
      renderCell: (params) => (
        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'created_at',
      headerName: t('versionManagement:table.created'),
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: t('versionManagement:table.actions'),
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" gap={0.5}>
          <Tooltip title={t('versionManagement:tooltips.edit')}>
            <IconButton
              size="small"
              onClick={() => handleOpenDialog(params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('versionManagement:tooltips.delete')}>
            <IconButton
              size="small"
              onClick={() => handleDelete(params.row.id)}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('versionManagement:tooltips.androidDownload')}>
            <IconButton
              size="small"
              href={params.row.android_download_url}
              target="_blank"
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('versionManagement:tooltips.iosDownload')}>
            <IconButton
              size="small"
              href={params.row.ios_download_url}
              target="_blank"
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  if (loading && versions.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
          {t('versionManagement:title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchVersions}
            disabled={loading}
            sx={{ minWidth: 'auto' }}
          >
            {t('versionManagement:refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' }, minWidth: 'auto' }}
          >
            {t('versionManagement:addNewVersion')}
          </Button>
        </Box>
      </Box>

      {/* Help Section */}
      <Accordion sx={{ mb: 3, backgroundColor: '#f5f9ff' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1}>
            <InfoIcon color="primary" />
            <Typography variant="subtitle1" fontWeight="bold">
              {t('versionManagement:help.title')}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={1}>
            <Alert severity="info" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
              <Typography variant="subtitle2" fontWeight="bold">
                {t('versionManagement:help.latestVersion')}
              </Typography>
            </Alert>
            <Alert severity="warning" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                {t('versionManagement:help.minSupported')}
              </Typography>
              <Typography variant="body2">
                {t('versionManagement:help.example')}
              </Typography>
            </Alert>
            <Alert severity="error" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
              <Typography variant="subtitle2" fontWeight="bold">
                {t('versionManagement:help.forceUpdate')}
              </Typography>
            </Alert>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Filters */}
      <Card sx={{ mb: 3, backgroundColor: 'background.paper' }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <TextField
                label={t('versionManagement:filters.searchVersions')}
                variant="outlined"
                size="small"
                fullWidth
                value={filterSearch}
                onChange={handleFilterSearchChange}
                placeholder={t('versionManagement:filters.searchPlaceholder')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('versionManagement:filters.platform')}</InputLabel>
                <Select
                  value={filterPlatform}
                  onChange={handleFilterPlatformChange}
                  label={t('versionManagement:filters.platform')}
                >
                  <MenuItem value="all">{t('versionManagement:filters.allPlatforms')}</MenuItem>
                  <MenuItem value="android">{t('versionManagement:filters.android')}</MenuItem>
                  <MenuItem value="ios">{t('versionManagement:filters.ios')}</MenuItem>
                  <MenuItem value="expo">{t('versionManagement:filters.expo')}</MenuItem>
                  <MenuItem value="web">{t('versionManagement:filters.web')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('versionManagement:filters.status')}</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={handleFilterStatusChange}
                  label={t('versionManagement:filters.status')}
                >
                  <MenuItem value="all">{t('versionManagement:filters.allStatus')}</MenuItem>
                  <MenuItem value="latest">{t('versionManagement:filters.latest')}</MenuItem>
                  <MenuItem value="required">{t('versionManagement:filters.required')}</MenuItem>
                  <MenuItem value="available">{t('versionManagement:filters.available')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Data Grid */}
      <Card sx={{ backgroundColor: 'background.paper' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{
            width: '100%',
            overflowX: 'auto',
            minHeight: 520,
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: 'rgba(0,0,0,0.5)',
            },
          }}>
            <DataGrid
              rows={versions}
              columns={columns}
              disableSelectionOnClick
              loading={loading}
              hideFooterPagination
              hideFooter
              getRowSpacing={() => ({ top: 2, bottom: 2 })}
              sx={{
                border: 'none',
                minWidth: 1200, // Ensure minimum width for all columns
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: '#DC143C',
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                },
                '& .MuiDataGrid-columnHeaderTitleContainer': {
                  justifyContent: 'center',
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid #e0e0e0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  padding: { xs: '8px 4px', sm: '8px 12px' }
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#FFF0F3',
                },
                '& .MuiDataGrid-row:nth-of-type(even)': {
                  backgroundColor: '#fafafa',
                }
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Custom Pagination */}
      <CustomPagination
        page={page}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        customRowsActive={customRowsActive}
        customRowsInput={customRowsInput}
        onCustomRowsInputChange={handleCustomRowsInputChange}
        onCustomRowsInputBlur={handleCustomRowsInputBlur}
      />

      {/* Add/Edit Version Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingVersion ? t('versionManagement:dialogs.editTitle') : t('versionManagement:dialogs.addTitle')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label={t('versionManagement:dialogs.versionCode')}
                  value={formData.version_code}
                  onChange={handleInputChange('version_code')}
                  placeholder={t('versionManagement:dialogs.versionCodePlaceholder')}
                  fullWidth
                  required
                />
              </Grid>
              
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label={t('versionManagement:dialogs.versionName')}
                  value={formData.version_name}
                  onChange={handleInputChange('version_name')}
                  placeholder={t('versionManagement:dialogs.versionNamePlaceholder')}
                  fullWidth
                  required
                />
              </Grid>
              
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('versionManagement:dialogs.platform')}</InputLabel>
                  <Select
                    value={formData.platform}
                    onChange={handleInputChange('platform')}
                    label={t('versionManagement:dialogs.platform')}
                  >
                    <MenuItem value="android">{t('versionManagement:filters.android')}</MenuItem>
                    <MenuItem value="ios">{t('versionManagement:filters.ios')}</MenuItem>
                    <MenuItem value="expo">{t('versionManagement:filters.expo')}</MenuItem>
                    <MenuItem value="web">{t('versionManagement:filters.web')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label={t('versionManagement:dialogs.minSupportedVersion')}
                  value={formData.min_supported_version}
                  onChange={handleInputChange('min_supported_version')}
                  placeholder={t('versionManagement:dialogs.minSupportedVersionPlaceholder')}
                  fullWidth
                />
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <TextField
                  label={t('versionManagement:dialogs.releaseNotes')}
                  value={formData.release_notes}
                  onChange={handleInputChange('release_notes')}
                  placeholder={t('versionManagement:dialogs.releaseNotesPlaceholder')}
                  multiline
                  rows={3}
                  fullWidth
                  required
                />
              </Grid>
              
              
              <Grid size={{ xs: 12 }}>
                <Box display="flex" gap={4} flexWrap="wrap">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_latest}
                        onChange={handleInputChange('is_latest')}
                      />
                    }
                    label={t('versionManagement:dialogs.latestVersion')}
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_required}
                        onChange={handleInputChange('is_required')}
                      />
                    }
                    label={t('versionManagement:dialogs.forceUpdateRequired')}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('versionManagement:dialogs.cancel')}</Button>
          <Button onClick={handleSave} variant="contained">
            {editingVersion ? t('versionManagement:dialogs.update') : t('versionManagement:dialogs.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VersionManagement;